/**
 * UIManager - Centralized DOM element caching and access
 * Eliminates repeated getElementById calls for better performance
 */

/** Cached DOM element references */
interface CachedElements {
  // Core canvas and loading
  canvas: HTMLCanvasElement | null;
  loading: HTMLElement | null;
  loadingStatus: HTMLElement | null;
  loadingProgressBar: HTMLElement | null;
  launchBtn: HTMLButtonElement | null;
  
  // HUD elements
  balance: HTMLElement | null;
  reputation: HTMLElement | null;
  missionsCompleted: HTMLElement | null;
  currentLevel: HTMLElement | null;
  gameDate: HTMLElement | null;
  asteroidCount: HTMLElement | null;
  
  // Time controls
  timeSlider: HTMLInputElement | null;
  timeDisplay: HTMLElement | null;
  
  // Info panels
  targetInfo: HTMLElement | null;
  hoverInfo: HTMLElement | null;
  activeMissionsList: HTMLElement | null;
  flightLog: HTMLElement | null;
  
  // Search
  bodySearch: HTMLInputElement | null;
  searchSuggestions: HTMLElement | null;
  
  // Left panel sections
  leftPanel: HTMLElement | null;
  techTreeSection: HTMLElement | null;
  assetsSection: HTMLElement | null;
  storageSection: HTMLElement | null;
  
  // Buttons
  planMissionBtn: HTMLButtonElement | null;
  saveUrlBtn: HTMLButtonElement | null;
  copySaveBtn: HTMLButtonElement | null;
  openTechTreeBtn: HTMLButtonElement | null;
  openAssetsBtn: HTMLButtonElement | null;
  openStorageBtn: HTMLButtonElement | null;
  
  // Modals
  helpModal: HTMLElement | null;
  techTreeModal: HTMLElement | null;
  assetsModal: HTMLElement | null;
  storageModal: HTMLElement | null;
  phaseModal: HTMLElement | null;
  levelUpModal: HTMLElement | null;
  marketDetailModal: HTMLElement | null;
  sellResourceModal: HTMLElement | null;
  
  // Help modal content
  helpWalkthrough: HTMLElement | null;
  helpProgressionList: HTMLElement | null;
  helpTipsList: HTMLElement | null;
  helpReferencesList: HTMLElement | null;
  helpTipText: HTMLElement | null;
  helpHeader: HTMLElement | null;
  
  // Phase modal content
  phaseModalContent: HTMLElement | null;
  phaseModalImage: HTMLImageElement | null;
  phaseModalTitle: HTMLElement | null;
  phaseModalAsteroidInfo: HTMLElement | null;
  phaseModalDescription: HTMLElement | null;
  phaseModalDetails: HTMLElement | null;
  
  // Level up modal content
  levelUpTitle: HTMLElement | null;
  levelUpDescription: HTMLElement | null;
  levelUpBadge: HTMLElement | null;
  levelUpUnlocks: HTMLElement | null;
  
  // Tech tree content
  techCategories: HTMLElement | null;
  techTreeContent: HTMLElement | null;
  techEffectsSummary: HTMLElement | null;
  techTreeStatus: HTMLElement | null;
  techTreeLocked: HTMLElement | null;
  techModalBalance: HTMLElement | null;
  techModalEffects: HTMLElement | null;
  
  // Assets content
  assetsCategories: HTMLElement | null;
  assetsContent: HTMLElement | null;
  assetsStatus: HTMLElement | null;
  assetsLocked: HTMLElement | null;
  assetsModalBalance: HTMLElement | null;
  ownedAssetsSummary: HTMLElement | null;
  
  // Storage content
  storageContent: HTMLElement | null;
  storageDepotsList: HTMLElement | null;
  storageSummary: HTMLElement | null;
  storageStatus: HTMLElement | null;
  storageLocked: HTMLElement | null;
  storageModalBalance: HTMLElement | null;
  
  // Market content
  marketPrices: HTMLElement | null;
  marketHigh: HTMLElement | null;
  marketLow: HTMLElement | null;
  marketDetailName: HTMLElement | null;
  marketDetailIcon: HTMLElement | null;
  marketCurrentPrice: HTMLElement | null;
  marketBasePrice: HTMLElement | null;
  marketChartCanvas: HTMLCanvasElement | null;
  
  // Breadcrumb
  breadcrumb: HTMLElement | null;
  breadcrumbLink: HTMLElement | null;
  
  // Save system
  saveUrlInput: HTMLInputElement | null;
}

/**
 * UIManager provides centralized, cached access to DOM elements
 */
export class UIManager {
  private elements: CachedElements;
  private initialized = false;
  
  constructor() {
    // Initialize with nulls - will be populated on init()
    this.elements = {
      canvas: null,
      loading: null,
      loadingStatus: null,
      loadingProgressBar: null,
      launchBtn: null,
      balance: null,
      reputation: null,
      missionsCompleted: null,
      currentLevel: null,
      gameDate: null,
      asteroidCount: null,
      timeSlider: null,
      timeDisplay: null,
      targetInfo: null,
      hoverInfo: null,
      activeMissionsList: null,
      flightLog: null,
      bodySearch: null,
      searchSuggestions: null,
      leftPanel: null,
      techTreeSection: null,
      assetsSection: null,
      storageSection: null,
      planMissionBtn: null,
      saveUrlBtn: null,
      copySaveBtn: null,
      openTechTreeBtn: null,
      openAssetsBtn: null,
      openStorageBtn: null,
      helpModal: null,
      techTreeModal: null,
      assetsModal: null,
      storageModal: null,
      phaseModal: null,
      levelUpModal: null,
      marketDetailModal: null,
      sellResourceModal: null,
      helpWalkthrough: null,
      helpProgressionList: null,
      helpTipsList: null,
      helpReferencesList: null,
      helpTipText: null,
      helpHeader: null,
      phaseModalContent: null,
      phaseModalImage: null,
      phaseModalTitle: null,
      phaseModalAsteroidInfo: null,
      phaseModalDescription: null,
      phaseModalDetails: null,
      levelUpTitle: null,
      levelUpDescription: null,
      levelUpBadge: null,
      levelUpUnlocks: null,
      techCategories: null,
      techTreeContent: null,
      techEffectsSummary: null,
      techTreeStatus: null,
      techTreeLocked: null,
      techModalBalance: null,
      techModalEffects: null,
      assetsCategories: null,
      assetsContent: null,
      assetsStatus: null,
      assetsLocked: null,
      assetsModalBalance: null,
      ownedAssetsSummary: null,
      storageContent: null,
      storageDepotsList: null,
      storageSummary: null,
      storageStatus: null,
      storageLocked: null,
      storageModalBalance: null,
      marketPrices: null,
      marketHigh: null,
      marketLow: null,
      marketDetailName: null,
      marketDetailIcon: null,
      marketCurrentPrice: null,
      marketBasePrice: null,
      marketChartCanvas: null,
      breadcrumb: null,
      breadcrumbLink: null,
      saveUrlInput: null,
    };
  }
  
  /**
   * Initialize and cache all DOM element references
   * Call this once after DOM is ready
   */
  init(): void {
    if (this.initialized) return;
    
    // Core canvas and loading
    this.elements.canvas = document.getElementById('canvas') as HTMLCanvasElement;
    this.elements.loading = document.getElementById('loading');
    this.elements.loadingStatus = document.getElementById('loading-status');
    this.elements.loadingProgressBar = document.getElementById('loading-progress-bar');
    this.elements.launchBtn = document.getElementById('launch-btn') as HTMLButtonElement;
    
    // HUD elements
    this.elements.balance = document.getElementById('balance');
    this.elements.reputation = document.getElementById('reputation');
    this.elements.missionsCompleted = document.getElementById('missions-completed');
    this.elements.currentLevel = document.getElementById('current-level');
    this.elements.gameDate = document.getElementById('game-date');
    this.elements.asteroidCount = document.getElementById('asteroid-count');
    
    // Time controls
    this.elements.timeSlider = document.getElementById('time-slider') as HTMLInputElement;
    this.elements.timeDisplay = document.getElementById('time-display');
    
    // Info panels
    this.elements.targetInfo = document.getElementById('target-info');
    this.elements.hoverInfo = document.getElementById('hover-info');
    this.elements.activeMissionsList = document.getElementById('active-missions-list');
    this.elements.flightLog = document.getElementById('flight-log');
    
    // Search
    this.elements.bodySearch = document.getElementById('body-search') as HTMLInputElement;
    this.elements.searchSuggestions = document.getElementById('search-suggestions');
    
    // Left panel sections
    this.elements.leftPanel = document.getElementById('left-panel');
    this.elements.techTreeSection = document.getElementById('tech-tree-section');
    this.elements.assetsSection = document.getElementById('assets-section');
    this.elements.storageSection = document.getElementById('storage-section');
    
    // Buttons
    this.elements.planMissionBtn = document.getElementById('plan-mission-btn') as HTMLButtonElement;
    this.elements.saveUrlBtn = document.getElementById('save-url-btn') as HTMLButtonElement;
    this.elements.copySaveBtn = document.getElementById('copy-save-btn') as HTMLButtonElement;
    this.elements.openTechTreeBtn = document.getElementById('open-tech-tree-btn') as HTMLButtonElement;
    this.elements.openAssetsBtn = document.getElementById('open-assets-btn') as HTMLButtonElement;
    this.elements.openStorageBtn = document.getElementById('open-storage-btn') as HTMLButtonElement;
    
    // Modals
    this.elements.helpModal = document.getElementById('help-modal');
    this.elements.techTreeModal = document.getElementById('tech-tree-modal');
    this.elements.assetsModal = document.getElementById('assets-modal');
    this.elements.storageModal = document.getElementById('storage-modal');
    this.elements.phaseModal = document.getElementById('phase-modal');
    this.elements.levelUpModal = document.getElementById('level-up-modal');
    this.elements.marketDetailModal = document.getElementById('market-detail-modal');
    this.elements.sellResourceModal = document.getElementById('sell-resource-modal');
    
    // Help modal content
    this.elements.helpWalkthrough = document.getElementById('help-walkthrough');
    this.elements.helpProgressionList = document.getElementById('help-progression-list');
    this.elements.helpTipsList = document.getElementById('help-tips-list');
    this.elements.helpReferencesList = document.getElementById('help-references-list');
    this.elements.helpTipText = document.getElementById('help-tip-text');
    this.elements.helpHeader = document.getElementById('help-header');
    
    // Phase modal content
    this.elements.phaseModalContent = document.getElementById('phase-modal-content');
    this.elements.phaseModalImage = document.getElementById('phase-modal-image') as HTMLImageElement;
    this.elements.phaseModalTitle = document.getElementById('phase-modal-title');
    this.elements.phaseModalAsteroidInfo = document.getElementById('phase-modal-asteroid-info');
    this.elements.phaseModalDescription = document.getElementById('phase-modal-description');
    this.elements.phaseModalDetails = document.getElementById('phase-modal-details');
    
    // Level up modal content
    this.elements.levelUpTitle = document.getElementById('level-up-title');
    this.elements.levelUpDescription = document.getElementById('level-up-description');
    this.elements.levelUpBadge = document.getElementById('level-up-badge');
    this.elements.levelUpUnlocks = document.getElementById('level-up-unlocks');
    
    // Tech tree content
    this.elements.techCategories = document.getElementById('tech-categories');
    this.elements.techTreeContent = document.getElementById('tech-tree-content');
    this.elements.techEffectsSummary = document.getElementById('tech-effects-summary');
    this.elements.techTreeStatus = document.getElementById('tech-tree-status');
    this.elements.techTreeLocked = document.getElementById('tech-tree-locked');
    this.elements.techModalBalance = document.getElementById('tech-modal-balance');
    this.elements.techModalEffects = document.getElementById('tech-modal-effects');
    
    // Assets content
    this.elements.assetsCategories = document.getElementById('assets-categories');
    this.elements.assetsContent = document.getElementById('assets-content');
    this.elements.assetsStatus = document.getElementById('assets-status');
    this.elements.assetsLocked = document.getElementById('assets-locked');
    this.elements.assetsModalBalance = document.getElementById('assets-modal-balance');
    this.elements.ownedAssetsSummary = document.getElementById('owned-assets-summary');
    
    // Storage content
    this.elements.storageContent = document.getElementById('storage-content');
    this.elements.storageDepotsList = document.getElementById('storage-depots-list');
    this.elements.storageSummary = document.getElementById('storage-summary');
    this.elements.storageStatus = document.getElementById('storage-status');
    this.elements.storageLocked = document.getElementById('storage-locked');
    this.elements.storageModalBalance = document.getElementById('storage-modal-balance');
    
    // Market content
    this.elements.marketPrices = document.getElementById('market-prices');
    this.elements.marketHigh = document.getElementById('market-high');
    this.elements.marketLow = document.getElementById('market-low');
    this.elements.marketDetailName = document.getElementById('market-detail-name');
    this.elements.marketDetailIcon = document.getElementById('market-detail-icon');
    this.elements.marketCurrentPrice = document.getElementById('market-current-price');
    this.elements.marketBasePrice = document.getElementById('market-base-price');
    this.elements.marketChartCanvas = document.getElementById('market-chart-canvas') as HTMLCanvasElement;
    
    // Breadcrumb
    this.elements.breadcrumb = document.getElementById('breadcrumb');
    this.elements.breadcrumbLink = document.getElementById('breadcrumb-link');
    
    // Save system
    this.elements.saveUrlInput = document.getElementById('save-url-input') as HTMLInputElement;
    
    this.initialized = true;
  }
  
  // ============================================
  // Typed getters for all cached elements
  // ============================================
  
  // Core
  get canvas(): HTMLCanvasElement | null { return this.elements.canvas; }
  get loading(): HTMLElement | null { return this.elements.loading; }
  get loadingStatus(): HTMLElement | null { return this.elements.loadingStatus; }
  get loadingProgressBar(): HTMLElement | null { return this.elements.loadingProgressBar; }
  get launchBtn(): HTMLButtonElement | null { return this.elements.launchBtn; }
  
  // HUD
  get balance(): HTMLElement | null { return this.elements.balance; }
  get reputation(): HTMLElement | null { return this.elements.reputation; }
  get missionsCompleted(): HTMLElement | null { return this.elements.missionsCompleted; }
  get currentLevel(): HTMLElement | null { return this.elements.currentLevel; }
  get gameDate(): HTMLElement | null { return this.elements.gameDate; }
  get asteroidCount(): HTMLElement | null { return this.elements.asteroidCount; }
  
  // Time
  get timeSlider(): HTMLInputElement | null { return this.elements.timeSlider; }
  get timeDisplay(): HTMLElement | null { return this.elements.timeDisplay; }
  
  // Info panels
  get targetInfo(): HTMLElement | null { return this.elements.targetInfo; }
  get hoverInfo(): HTMLElement | null { return this.elements.hoverInfo; }
  get activeMissionsList(): HTMLElement | null { return this.elements.activeMissionsList; }
  get flightLog(): HTMLElement | null { return this.elements.flightLog; }
  
  // Search
  get bodySearch(): HTMLInputElement | null { return this.elements.bodySearch; }
  get searchSuggestions(): HTMLElement | null { return this.elements.searchSuggestions; }
  
  // Left panel
  get leftPanel(): HTMLElement | null { return this.elements.leftPanel; }
  get techTreeSection(): HTMLElement | null { return this.elements.techTreeSection; }
  get assetsSection(): HTMLElement | null { return this.elements.assetsSection; }
  get storageSection(): HTMLElement | null { return this.elements.storageSection; }
  
  // Buttons
  get planMissionBtn(): HTMLButtonElement | null { return this.elements.planMissionBtn; }
  get saveUrlBtn(): HTMLButtonElement | null { return this.elements.saveUrlBtn; }
  get copySaveBtn(): HTMLButtonElement | null { return this.elements.copySaveBtn; }
  get openTechTreeBtn(): HTMLButtonElement | null { return this.elements.openTechTreeBtn; }
  get openAssetsBtn(): HTMLButtonElement | null { return this.elements.openAssetsBtn; }
  get openStorageBtn(): HTMLButtonElement | null { return this.elements.openStorageBtn; }
  
  // Modals
  get helpModal(): HTMLElement | null { return this.elements.helpModal; }
  get techTreeModal(): HTMLElement | null { return this.elements.techTreeModal; }
  get assetsModal(): HTMLElement | null { return this.elements.assetsModal; }
  get storageModal(): HTMLElement | null { return this.elements.storageModal; }
  get phaseModal(): HTMLElement | null { return this.elements.phaseModal; }
  get levelUpModal(): HTMLElement | null { return this.elements.levelUpModal; }
  get marketDetailModal(): HTMLElement | null { return this.elements.marketDetailModal; }
  get sellResourceModal(): HTMLElement | null { return this.elements.sellResourceModal; }
  
  // Help modal content
  get helpWalkthrough(): HTMLElement | null { return this.elements.helpWalkthrough; }
  get helpProgressionList(): HTMLElement | null { return this.elements.helpProgressionList; }
  get helpTipsList(): HTMLElement | null { return this.elements.helpTipsList; }
  get helpReferencesList(): HTMLElement | null { return this.elements.helpReferencesList; }
  get helpTipText(): HTMLElement | null { return this.elements.helpTipText; }
  get helpHeader(): HTMLElement | null { return this.elements.helpHeader; }
  
  // Phase modal content
  get phaseModalContent(): HTMLElement | null { return this.elements.phaseModalContent; }
  get phaseModalImage(): HTMLImageElement | null { return this.elements.phaseModalImage; }
  get phaseModalTitle(): HTMLElement | null { return this.elements.phaseModalTitle; }
  get phaseModalAsteroidInfo(): HTMLElement | null { return this.elements.phaseModalAsteroidInfo; }
  get phaseModalDescription(): HTMLElement | null { return this.elements.phaseModalDescription; }
  get phaseModalDetails(): HTMLElement | null { return this.elements.phaseModalDetails; }
  
  // Level up modal content
  get levelUpTitle(): HTMLElement | null { return this.elements.levelUpTitle; }
  get levelUpDescription(): HTMLElement | null { return this.elements.levelUpDescription; }
  get levelUpBadge(): HTMLElement | null { return this.elements.levelUpBadge; }
  get levelUpUnlocks(): HTMLElement | null { return this.elements.levelUpUnlocks; }
  
  // Tech tree content
  get techCategories(): HTMLElement | null { return this.elements.techCategories; }
  get techTreeContent(): HTMLElement | null { return this.elements.techTreeContent; }
  get techEffectsSummary(): HTMLElement | null { return this.elements.techEffectsSummary; }
  get techTreeStatus(): HTMLElement | null { return this.elements.techTreeStatus; }
  get techTreeLocked(): HTMLElement | null { return this.elements.techTreeLocked; }
  get techModalBalance(): HTMLElement | null { return this.elements.techModalBalance; }
  get techModalEffects(): HTMLElement | null { return this.elements.techModalEffects; }
  
  // Assets content
  get assetsCategories(): HTMLElement | null { return this.elements.assetsCategories; }
  get assetsContent(): HTMLElement | null { return this.elements.assetsContent; }
  get assetsStatus(): HTMLElement | null { return this.elements.assetsStatus; }
  get assetsLocked(): HTMLElement | null { return this.elements.assetsLocked; }
  get assetsModalBalance(): HTMLElement | null { return this.elements.assetsModalBalance; }
  get ownedAssetsSummary(): HTMLElement | null { return this.elements.ownedAssetsSummary; }
  
  // Storage content
  get storageContent(): HTMLElement | null { return this.elements.storageContent; }
  get storageDepotsList(): HTMLElement | null { return this.elements.storageDepotsList; }
  get storageSummary(): HTMLElement | null { return this.elements.storageSummary; }
  get storageStatus(): HTMLElement | null { return this.elements.storageStatus; }
  get storageLocked(): HTMLElement | null { return this.elements.storageLocked; }
  get storageModalBalance(): HTMLElement | null { return this.elements.storageModalBalance; }
  
  // Market content
  get marketPrices(): HTMLElement | null { return this.elements.marketPrices; }
  get marketHigh(): HTMLElement | null { return this.elements.marketHigh; }
  get marketLow(): HTMLElement | null { return this.elements.marketLow; }
  get marketDetailName(): HTMLElement | null { return this.elements.marketDetailName; }
  get marketDetailIcon(): HTMLElement | null { return this.elements.marketDetailIcon; }
  get marketCurrentPrice(): HTMLElement | null { return this.elements.marketCurrentPrice; }
  get marketBasePrice(): HTMLElement | null { return this.elements.marketBasePrice; }
  get marketChartCanvas(): HTMLCanvasElement | null { return this.elements.marketChartCanvas; }
  
  // Breadcrumb
  get breadcrumb(): HTMLElement | null { return this.elements.breadcrumb; }
  get breadcrumbLink(): HTMLElement | null { return this.elements.breadcrumbLink; }
  
  // Save system
  get saveUrlInput(): HTMLInputElement | null { return this.elements.saveUrlInput; }
  
  // ============================================
  // Helper methods for common operations
  // ============================================
  
  /** Show a modal by adding 'visible' class */
  showModal(modal: HTMLElement | null): void {
    modal?.classList.add('visible');
  }
  
  /** Hide a modal by removing 'visible' class */
  hideModal(modal: HTMLElement | null): void {
    modal?.classList.remove('visible');
  }
  
  /** Set text content safely */
  setText(element: HTMLElement | null, text: string): void {
    if (element) element.textContent = text;
  }
  
  /** Set innerHTML safely */
  setHTML(element: HTMLElement | null, html: string): void {
    if (element) element.innerHTML = html;
  }
  
  /** Show an element */
  show(element: HTMLElement | null): void {
    if (element) element.style.display = '';
  }
  
  /** Hide an element */
  hide(element: HTMLElement | null): void {
    if (element) element.style.display = 'none';
  }
  
  /** Toggle visibility class */
  toggleVisible(element: HTMLElement | null, visible: boolean): void {
    if (!element) return;
    if (visible) {
      element.classList.add('visible');
    } else {
      element.classList.remove('visible');
    }
  }
}

// Singleton instance
let uiManagerInstance: UIManager | null = null;

/** Get the singleton UIManager instance */
export function getUIManager(): UIManager {
  if (!uiManagerInstance) {
    uiManagerInstance = new UIManager();
  }
  return uiManagerInstance;
}
